import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Globe, Users, Activity, ArrowRight, ShieldCheck } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Card } from "@/components/ui/card";
import { TenantStatusBadge, TenantPackageBadge } from "./tenants/TenantBadges";

export default function PlatformDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/platform/dashboard")
      .then((r) => setData(r.data))
      .catch(notifyError)
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return <PlatformLayout title="Dashboard"><div className="text-white/60">Memuat...</div></PlatformLayout>;
  }

  const stats = [
    { label: "Total Website", value: data.total_website, icon: Globe },
    { label: "Active Website", value: data.active_website, icon: Activity, tone: "good" },
    { label: "Inactive Website", value: data.inactive_website, icon: Activity, tone: "muted" },
    { label: "Total Tenant", value: data.total_tenant, icon: Building2 },
    { label: "Total Admin", value: data.total_admin, icon: Users },
    { label: "Platform Owner", value: data.total_platform_owner, icon: ShieldCheck },
  ];

  return (
    <PlatformLayout title="Dashboard">
      <div data-testid="platform-dashboard-page">
        {/* Platform status pill */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="text-xs text-white/50">Status:</span>
          <span
            className={`text-[11px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider ${
              data.platform_status === "operational" ? "bg-emerald-500/15 text-emerald-400" :
              data.platform_status === "maintenance" ? "bg-amber-500/15 text-amber-400" :
              "bg-red-500/15 text-red-400"
            }`}
            data-testid="platform-status-badge"
          >
            {data.platform_status}
          </span>
          <span className="text-xs text-white/50">•</span>
          <span className="text-xs text-white/70" data-testid="dashboard-platform-name">{data.platform_name}</span>
          <span className="text-[10px] text-white/40 ml-1">v{data.platform_version}</span>
        </div>

        {/* Stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <Card key={s.label} className="bg-white/5 border-white/10 p-4 rounded-2xl">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 20%, transparent)" }}>
                    <Icon size={16} className="brand-text-primary" />
                  </div>
                </div>
                <div className="text-3xl font-black text-white font-display">{s.value}</div>
                <div className="text-xs text-white/50 mt-1">{s.label}</div>
              </Card>
            );
          })}
        </div>

        {/* Tenants preview */}
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-display font-black text-white">Tenant Aktif</h2>
            <p className="text-xs text-white/50 mt-0.5">Semua tenant yang saat ini di-provisioning di platform.</p>
          </div>
          <Link to="/platform/tenants" className="text-xs brand-text-primary font-bold inline-flex items-center gap-1 hover:underline" data-testid="dashboard-view-all-tenants">
            Lihat Semua <ArrowRight size={12} />
          </Link>
        </div>
        <div className="space-y-2">
          {(data.tenants_preview || []).map((t) => (
            <Link
              key={t.tenant_id}
              to={`/platform/tenants/${t.tenant_id}`}
              className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
              data-testid={`dashboard-tenant-${t.tenant_id}`}
            >
              <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 20%, transparent)" }}>
                <Building2 size={16} className="brand-text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white truncate">{t.tenant_name}</div>
                <div className="text-[11px] text-white/40 font-mono">{t.tenant_code}</div>
              </div>
              <div className="flex items-center gap-2">
                <TenantStatusBadge status={t.tenant_status} size="sm" />
                {t.package && <TenantPackageBadge package={t.package} size="sm" />}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PlatformLayout>
  );
}
